/**
 * LES PRÉVISIONS, CÔTÉ ÉCRAN — lot 98, 18/09/2026.
 *
 * Ne dessine rien, n'appelle rien. Transforme la lecture de
 * `GET /analyse/previsions` en lignes prévues, propositions et courbe, avec le
 * calcul partagé de `@stafa/core`.
 */

import {
  ajouterJours,
  centimes,
  HORIZON_SEMAINES,
  lignesPrevues,
  lundiDe,
  partDe,
  propositions,
  semaineIso,
  type EcartDeLigne,
  type LignePrevue,
  type PieceReelle,
  type Prevision,
  type Propositions,
  type Rythme,
} from '@stafa/core';

export type Cote = 'client' | 'fournisseur';

export interface PartLue {
  readonly centre_id: string | null;
  readonly valeur: string;
}

export interface PrevisionLue {
  readonly id: string;
  readonly cote: Cote;
  readonly type: 'contrat' | 'estimation';
  readonly tiers_pennylane_id: string | null;
  readonly tiers_nom: string;
  readonly libelle: string | null;
  readonly famille: 'exploitation' | 'structure' | null;
  readonly montant_ht: string;
  readonly taux_tva: string;
  readonly rythme: Rythme;
  readonly jour_semaine: number | null;
  readonly jour_mois: number | null;
  readonly fin_de_mois: boolean;
  readonly mois_depart: number | null;
  readonly du: string;
  readonly au: string | null;
  readonly delai_jours: number;
  readonly prelevement: boolean;
  readonly remplace_transports: boolean;
  readonly repartition: '%' | '€';
  readonly actif: boolean;
  readonly cree_par: string;
  /** Jour de création, `AAAA-MM-JJ`. */
  readonly cree_le: string;
  readonly parts: readonly PartLue[];
}

export interface PrevisionsLues {
  readonly aujourdhui: string;
  readonly activites: readonly { id: string; nom: string; actif: boolean }[];
  readonly tiers: Readonly<Record<Cote, readonly { id: string; nom: string }[]>>;
  readonly previsions: readonly PrevisionLue[];
  readonly ecarts: readonly {
    prevision_id: string;
    date_rythme: string;
    decalee_au: string | null;
    supprimee: boolean;
    par: string;
  }[];
  readonly rapprochements: readonly {
    prevision_id: string;
    date_rythme: string;
    cote: Cote;
    pennylane_id: string;
    numero: string | null;
    date_reelle: string | null;
    montant_prevu: string;
    montant_reel: string;
    par: string;
    le: string;
  }[];
  readonly refus: readonly {
    prevision_id: string;
    date_rythme: string;
    pennylane_id: string;
  }[];
  readonly pieces: readonly {
    cote: Cote;
    pennylane_id: string;
    tiers_pennylane_id: string | null;
    numero: string | null;
    date: string;
    ttc: string;
  }[];
  readonly retards: Readonly<Record<string, number | null>>;
}

export function versPrevision(l: PrevisionLue): Prevision {
  const parts: Record<string, number> = {};
  for (const x of l.parts) {
    parts[x.centre_id ?? ''] =
      l.repartition === '%' ? Math.round(Number(x.valeur) * 100) : centimes(x.valeur);
  }
  return {
    id: l.id,
    cote: l.cote,
    type: l.type,
    tiers_pennylane_id: l.tiers_pennylane_id,
    tiers_nom: l.tiers_nom,
    montant_ht_centimes: centimes(l.montant_ht),
    taux_tva: Number(l.taux_tva),
    rythme: l.rythme,
    jour_semaine: l.jour_semaine,
    jour_mois: l.jour_mois,
    fin_de_mois: l.fin_de_mois,
    mois_depart: l.mois_depart,
    du: l.du,
    au: l.au,
    delai_jours: l.delai_jours,
    prelevement: l.prelevement,
    actif: l.actif,
    repartition: l.repartition,
    cree_le: l.cree_le,
    parts,
  };
}

/** La fenêtre : 45 jours derrière (rapprochement), 13 semaines devant. */
export function fenetre(aujourdhui: string): {
  depuis: string;
  jusqua: string;
  lundi: string;
} {
  const lundi = lundiDe(aujourdhui);
  return {
    depuis: ajouterJours(aujourdhui, -45),
    jusqua: ajouterJours(lundi, 7 * HORIZON_SEMAINES - 1),
    lundi,
  };
}

export interface Calcul {
  readonly previsions: ReadonlyMap<string, Prevision>;
  readonly lignes: readonly LignePrevue[];
  readonly propositions: Propositions;
}

export function calculer(d: PrevisionsLues): Calcul {
  const f = fenetre(d.aujourdhui);
  const previsions = new Map(d.previsions.map((p) => [p.id, versPrevision(p)]));
  const ecarts = new Map<string, EcartDeLigne>(
    d.ecarts.map((e) => [`${e.prevision_id}|${e.date_rythme}`, e]),
  );
  const rapprochees = new Set(
    d.rapprochements.map((r) => `${r.prevision_id}|${r.date_rythme}`),
  );
  const retards = new Map(Object.entries(d.retards));
  const lignes = lignesPrevues(
    [...previsions.values()],
    ecarts,
    rapprochees,
    retards,
    f.depuis,
    f.jusqua,
    d.aujourdhui,
  );
  const refus = new Set(
    d.refus.map((r) => `${r.prevision_id}|${r.date_rythme}|${r.pennylane_id}`),
  );
  // Chaque pièce n'est candidate que pour les prévisions de son côté.
  const piecesParCote = (c: Cote): PieceReelle[] =>
    d.pieces
      .filter((p) => p.cote === c)
      .map((p) => ({
        pennylane_id: p.pennylane_id,
        tiers_pennylane_id: p.tiers_pennylane_id,
        numero: p.numero,
        date: p.date,
        ttc_centimes: centimes(p.ttc),
      }));
  const parCote = (c: Cote) =>
    propositions(
      lignes.filter((l) => previsions.get(l.prevision_id)?.cote === c),
      previsions,
      piecesParCote(c),
      refus,
      d.aujourdhui,
    );
  const pc = parCote('client');
  const pf = parCote('fournisseur');
  return {
    previsions,
    lignes,
    propositions: {
      a_confirmer: [...pc.a_confirmer, ...pf.a_confirmer].sort((a, b) =>
        a.ligne.date < b.ligne.date ? -1 : 1,
      ),
      sans_piece: [...pc.sans_piece, ...pf.sans_piece].sort((a, b) =>
        a.date < b.date ? -1 : 1,
      ),
    },
  };
}

/** Le montant d'une ligne vu depuis une activité (`tout`, un id, `''`). */
export function montantVu(l: LignePrevue, p: Prevision, activite: string): number {
  if (activite === 'tout') return l.ttc_centimes;
  return Math.round(l.ttc_centimes * partDe(p, activite));
}

export interface SemainePrevue {
  readonly lundi: string;
  readonly numero: number;
  readonly encaissements: number;
  readonly decaissements: number;
}

/** Les 13 semaines à venir, encaissements et décaissements à la date de règlement. */
export function semainesPrevues(
  c: Calcul,
  aujourdhui: string,
  activite: string,
): { semaines: SemainePrevue[]; au_dela: { enc: number; dec: number } } {
  const { lundi } = fenetre(aujourdhui);
  const semaines = Array.from({ length: HORIZON_SEMAINES }, (_, i) => {
    const l = ajouterJours(lundi, 7 * i);
    return { lundi: l, numero: semaineIso(l), encaissements: 0, decaissements: 0 };
  });
  const au_dela = { enc: 0, dec: 0 };
  const fin = ajouterJours(lundi, 7 * HORIZON_SEMAINES - 1);
  for (const l of c.lignes) {
    const p = c.previsions.get(l.prevision_id);
    if (p === undefined) continue;
    const v = montantVu(l, p, activite);
    if (v === 0) continue;
    // Une ligne réglée avant la semaine en cours tombe dans la semaine en
    // cours : elle n'est pas encore faite (même règle que l'échéancier).
    const date = l.reglement < lundi ? lundi : l.reglement;
    const enc = p.cote === 'client';
    if (date > fin) {
      if (enc) au_dela.enc += v;
      else au_dela.dec += v;
      continue;
    }
    const s =
      semaines[Math.floor((Date.parse(date) - Date.parse(lundi)) / (7 * 86400000))];
    if (s === undefined) continue;
    if (enc) s.encaissements += v;
    else s.decaissements += v;
  }
  return { semaines, au_dela };
}

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

export function rythmeEnMots(p: PrevisionLue): string {
  switch (p.rythme) {
    case 'hebdo':
      return `chaque ${JOURS[p.jour_semaine ?? 0] ?? ''}`;
    case 'mensuel':
      return p.fin_de_mois
        ? 'chaque fin de mois'
        : `le ${String(p.jour_mois)} de chaque mois`;
    case 'trimestriel':
      return `${p.fin_de_mois ? 'en fin de mois' : `le ${String(p.jour_mois)}`}, tous les 3 mois (à partir de ${MOIS[(p.mois_depart ?? 1) - 1] ?? ''})`;
    default:
      return `une fois, le ${dateFr(p.du)}`;
  }
}

export function reglementEnMots(p: PrevisionLue, retard: number | null): string {
  if (p.cote === 'client') {
    const r = Math.max(0, retard ?? 0);
    return `échéance à ${String(p.delai_jours)} j + retard habituel ${String(r)} j`;
  }
  if (p.prelevement) {
    return p.delai_jours > 0
      ? `prélevé ${String(p.delai_jours)} j après`
      : 'prélevé le jour même';
  }
  return p.delai_jours > 0 ? `payé à ${String(p.delai_jours)} j` : 'payé le jour même';
}

export function dateFr(iso: string | null): string {
  return iso === null
    ? '—'
    : `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export function euros(c: number, centimesAussi = true): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    ...(centimesAussi ? {} : { maximumFractionDigits: 0 }),
  }).format(c / 100);
}

/** « +18,6 % », « −8,8 % ». */
export function pourcent(relatif: number): string {
  const v = (relatif * 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return relatif > 0 ? `+${v} %` : relatif < 0 ? `${v.replace('-', '−')} %` : '0,0 %';
}

/** La saisie du formulaire, en texte comme les champs. */
export interface Formulaire {
  type: 'contrat' | 'estimation';
  tiers_pennylane_id: string;
  tiers_nom: string;
  libelle: string;
  famille: '' | 'exploitation' | 'structure';
  montant_ht: string;
  taux_tva: string;
  rythme: Rythme;
  jour_semaine: string;
  jour_mois: string;
  mois_depart: string;
  du: string;
  au: string;
  delai_jours: string;
  prelevement: boolean;
  remplace_transports: boolean;
  repartition: '%' | '€';
  actif: boolean;
  parts: { centre_id: string; valeur: string }[];
}

/** `jour_mois` vaut `fin` pour la fin de mois. */
export function formulaireVide(cote: Cote, aujourdhui: string): Formulaire {
  return {
    type: 'contrat',
    tiers_pennylane_id: '',
    tiers_nom: '',
    libelle: '',
    famille: cote === 'fournisseur' ? 'exploitation' : '',
    montant_ht: '',
    taux_tva: '20',
    rythme: 'mensuel',
    jour_semaine: '4',
    jour_mois: '1',
    mois_depart: '1',
    du: aujourdhui,
    au: '',
    delai_jours: cote === 'client' ? '45' : '0',
    prelevement: false,
    remplace_transports: true,
    repartition: '%',
    actif: true,
    parts: [{ centre_id: '', valeur: '100' }],
  };
}

export function formulaireDe(p: PrevisionLue): Formulaire {
  return {
    type: p.type,
    tiers_pennylane_id: p.tiers_pennylane_id ?? '',
    tiers_nom: p.tiers_nom,
    libelle: p.libelle ?? '',
    famille: p.famille ?? '',
    montant_ht: p.montant_ht,
    taux_tva: String(Number(p.taux_tva)),
    rythme: p.rythme,
    jour_semaine: String(p.jour_semaine ?? 4),
    jour_mois: p.fin_de_mois ? 'fin' : String(p.jour_mois ?? 1),
    mois_depart: String(p.mois_depart ?? 1),
    du: p.du,
    au: p.rythme === 'une_fois' ? '' : (p.au ?? ''),
    delai_jours: String(p.delai_jours),
    prelevement: p.prelevement,
    remplace_transports: p.remplace_transports,
    repartition: p.repartition,
    actif: p.actif,
    parts: p.parts.map((x) => ({ centre_id: x.centre_id ?? '', valeur: x.valeur })),
  };
}

const virgule = (t: string) => t.trim().replace(/\s/g, '').replace(',', '.');

/** Le corps envoyé à l'API. `centre_id` vide : la structure. */
export function corpsDe(f: Formulaire, cote: Cote): Record<string, unknown> {
  const mensuel = f.rythme === 'mensuel' || f.rythme === 'trimestriel';
  return {
    cote,
    type: f.type,
    tiers_pennylane_id: f.tiers_pennylane_id === '' ? null : f.tiers_pennylane_id,
    tiers_nom: f.tiers_nom,
    libelle: f.libelle.trim() === '' ? null : f.libelle,
    famille: cote === 'fournisseur' && f.famille !== '' ? f.famille : null,
    montant_ht: virgule(f.montant_ht),
    taux_tva: virgule(f.taux_tva),
    rythme: f.rythme,
    jour_semaine: f.rythme === 'hebdo' ? Number(f.jour_semaine) : null,
    jour_mois: mensuel && f.jour_mois !== 'fin' ? Number(f.jour_mois) : null,
    fin_de_mois: mensuel && f.jour_mois === 'fin',
    mois_depart: f.rythme === 'trimestriel' ? Number(f.mois_depart) : null,
    du: f.du,
    au: f.rythme === 'une_fois' || f.au === '' ? null : f.au,
    delai_jours: Number(f.delai_jours === '' ? '0' : f.delai_jours),
    prelevement: cote === 'fournisseur' && f.prelevement,
    remplace_transports: cote === 'client' && f.remplace_transports,
    repartition: f.repartition,
    actif: f.actif,
    parts: f.parts.map((x) => ({
      centre_id: x.centre_id === '' ? null : x.centre_id,
      valeur: virgule(x.valeur),
    })),
  };
}

/** Le total de la répartition saisie, et ce qu'il doit faire. */
export function totalRepartition(f: Formulaire): { total: number; cible: number } {
  const total = f.parts.reduce((s, x) => s + centimes(virgule(x.valeur)), 0);
  return {
    total,
    cible: f.repartition === '%' ? 10000 : centimes(virgule(f.montant_ht)),
  };
}
